import React, { useEffect, useRef } from 'react';
import GoogleOAuthMark from './GoogleOAuthMark';

export function maskEmail(value) {
  const trimmed = String(value || '').trim();
  const at = trimmed.indexOf('@');
  if (at <= 0 || at === trimmed.length - 1) return 'm***';
  return `${trimmed.charAt(0)}***${trimmed.slice(at)}`;
}

export default function MagicLinkPostSendModal({
  email,
  open,
  resendCount = 1,
  showGoogleOAuth = false,
  googleOAuthHref = '',
  onUseDifferentEmail,
  onResend,
  sending = false,
  resendDisabled = false,
  resendLabel = '',
}) {
  const firstActionRef = useRef(null);
  const repeated = Number(resendCount) >= 2;
  const modalTitle = repeated
    ? 'Still not seeing it?'
    : 'Check your email to finish signing in';
  const googleHref = String(googleOAuthHref || '').trim();
  const canUseGoogle = Boolean(showGoogleOAuth && googleHref);
  const resendUnavailable = Boolean(sending || resendDisabled);

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => {
      firstActionRef.current?.focus?.();
    }, 0);
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onUseDifferentEmail?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onUseDifferentEmail]);

  if (!open) return null;

  const actionBaseStyle = {
    width: '100%',
    minHeight: 44,
    padding: '11px 14px',
    borderRadius: 12,
    fontWeight: 800,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    boxSizing: 'border-box',
    textDecoration: 'none',
    lineHeight: 1.2,
    whiteSpace: 'normal',
    cursor: 'pointer',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2100,
        background: 'rgba(5,9,18,0.78)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      data-testid="magic-link-post-send-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="magic-link-post-send-title"
        data-testid="magic-link-post-send-modal"
        style={{
          width: '100%',
          maxWidth: 430,
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          borderRadius: 18,
          border: '1px solid rgba(148,163,184,0.26)',
          background: '#0f172a',
          color: '#e8edf7',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          padding: '22px 20px',
        }}
      >
        <h2
          id="magic-link-post-send-title"
          data-testid="magic-link-post-send-title"
          style={{
            margin: '0 0 10px',
            fontSize: 20,
            lineHeight: 1.2,
            color: '#f8fbff',
          }}
        >
          {modalTitle}
        </h2>

        {repeated ? (
          <div style={{ color: '#c8d4eb', fontSize: 14, lineHeight: 1.55 }}>
            <p style={{ margin: '0 0 12px' }}>
              Sending another link usually does not help if the email is going
              to Spam/Junk or the address is wrong.
            </p>
            <p style={{ margin: '0 0 18px' }}>
              Check Spam, Junk, Promotions, confirm the email spelling
              {canUseGoogle ? ', or use Google instead.' : '.'}
            </p>
          </div>
        ) : (
          <div style={{ color: '#c8d4eb', fontSize: 14, lineHeight: 1.55 }}>
            <p style={{ margin: '0 0 6px' }}>
              We sent a secure sign-in link to:
            </p>
            <div
              style={{
                margin: '0 0 12px',
                padding: '9px 10px',
                borderRadius: 10,
                border: '1px solid rgba(148,163,184,0.22)',
                background: 'rgba(15,23,42,0.78)',
                color: '#f8fbff',
                fontWeight: 800,
                overflowWrap: 'anywhere',
              }}
            >
              {maskEmail(email)}
            </div>
            <p style={{ margin: '0 0 12px' }}>
              Open your email and tap the link to continue.
            </p>
            <p style={{ margin: '0 0 12px' }}>
              <strong style={{ color: '#f8fbff' }}>Can't find it?</strong>
              <br />
              Check Spam, Junk, Promotions, or search for "JG Support" or
              "support@joegalaxy.net".
            </p>
            <p style={{ margin: '0 0 18px' }}>
              <strong style={{ color: '#f8fbff' }}>Still stuck?</strong>
              <br />
              {canUseGoogle
                ? 'Use Google instead or try a different email.'
                : 'Try a different email.'}
            </p>
          </div>
        )}

        <div style={{ display: 'grid', gap: 8 }}>
          {canUseGoogle ? (
            <a
              ref={firstActionRef}
              href={googleHref}
              data-testid="magic-link-post-send-google"
              data-auth-modal-action="primary"
              style={{
                ...actionBaseStyle,
                background:
                  'linear-gradient(135deg, rgba(25,147,255,0.98), rgba(79,106,255,0.98))',
                border: 'none',
                color: '#fff',
              }}
            >
              <GoogleOAuthMark
                style={{ width: 18, height: 18, flex: '0 0 auto' }}
              />
              <span>Continue with Google</span>
            </a>
          ) : null}
          <button
            ref={canUseGoogle ? null : firstActionRef}
            type="button"
            onClick={onUseDifferentEmail}
            data-testid="magic-link-post-send-different-email"
            data-auth-modal-action="secondary"
            style={{
              ...actionBaseStyle,
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.26)',
              color: '#e8edf7',
            }}
          >
            Use a different email
          </button>
          <button
            type="button"
            onClick={onResend}
            disabled={resendUnavailable}
            data-testid="magic-link-post-send-resend"
            data-auth-modal-action="tertiary"
            style={{
              ...actionBaseStyle,
              minHeight: 40,
              background: 'transparent',
              border: '1px solid rgba(148,163,184,0.18)',
              color: '#9fb6d1',
              fontWeight: 700,
              opacity: resendUnavailable ? 0.62 : 1,
              cursor: resendUnavailable ? 'not-allowed' : 'pointer',
            }}
          >
            {sending
              ? 'Sending...'
              : resendLabel || (repeated ? 'Send again anyway' : 'Resend email')}
          </button>
        </div>
      </div>
    </div>
  );
}
